import { useState, useEffect, useRef } from 'react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import { motion, AnimatePresence } from 'framer-motion';
import mascot2 from '../assets/mascot-2.jpg';
import { 
  X, 
  Lock, 
  User, 
  Check, 
  AlertCircle, 
  Phone, 
  ArrowLeft, 
  GraduationCap, 
  Eye, 
  EyeOff, 
  Sparkles 
} from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (redirectUrl?: string) => void;
}

type AuthState = 
  | 'login' 
  | 'register-welcome'
  | 'register-class'
  | 'register-group'
  | 'register-batch'
  | 'register-institution'
  | 'register-gender'
  | 'register-info' 
  | 'otp' 
  | 'register-final' 
  | 'forgot-mobile' 
  | 'forgot-final' 
  | 'celebration'
  | '2fa';



export default function AuthModal({ isOpen, onClose, onLoginSuccess }: AuthModalProps) {
  // State Machine
  const [state, setState] = useState<AuthState>('login');
  const [authContext, setAuthContext] = useState<'register' | 'forgot'>('register');
  
  // Fields State
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [optionalSubject, setOptionalSubject] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [gender, setGender] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [userId, setUserId] = useState<string | number>('');
  const [mobileHint, setMobileHint] = useState('');

  // UI Status
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // OTP Timer Cooldown (120 seconds)
  const [countdown, setCountdown] = useState(120);
  const timerRef = useRef<NodeJS.Timeout | null>(null);



  // Manage Countdown timer
  useEffect(() => {
    if (state === 'otp' && countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state, countdown]);

  // Restart timer helper
  const startOtpCooldown = () => {
    setCountdown(120);
  };

  // Reset all fields when opening/closing
  useEffect(() => {
    if (isOpen) {
      setState('login');
      setError('');
      setSuccessMsg('');
      setMobile('');
      setPassword('');
      setConfirmPassword('');
      setName('');
      setSelectedClass('');
      setSelectedBatch('');
      setSelectedGroup('');
      setOptionalSubject('');
      setOtpCode('');
      setCountdown(120);
    }
  }, [isOpen]);

  const triggerDomFill = (mobileVal: string, passwordVal: string) => {
    setError('');
    setSuccessMsg('');
    setMobile(mobileVal);
    setPassword(passwordVal);
    
    // Dispatch native input/change events using React value setter prototype bypass
    setTimeout(() => {
      const setReactInputValue = (input: HTMLInputElement, value: string) => {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(input, value);
        } else {
          input.value = value;
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const mobileInput = document.querySelector('input[type="tel"]') as HTMLInputElement;
      if (mobileInput) {
        setReactInputValue(mobileInput, mobileVal);
      }
      
      const passwordInput = document.querySelector('input[type="password"]') as HTMLInputElement;
      if (passwordInput) {
        setReactInputValue(passwordInput, passwordVal);
      }
    }, 20);
  };

  // Form Auto-fill Demo Helpers
  const handleAutoFill = () => {
    setError('');
    setSuccessMsg('');
    if (state === 'login') {
      triggerDomFill('01711111111', 'pass1234');
    } else if (state === 'register-info') {
      setName('রাহুল আহমেদ');
      setMobile('01711111111');
      setTimeout(() => {
        const inputs = document.querySelectorAll('input');
        inputs.forEach((input: any) => {
          if (input.placeholder?.includes('রাহুল')) {
            input.value = 'রাহুল আহমেদ';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          } else if (input.type === 'tel' || input.placeholder?.includes('017')) {
            input.value = '01711111111';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }, 0);
    } else if (state === 'otp') {
      setOtpCode('123456');
      setTimeout(() => {
        const inputs = document.querySelectorAll('input');
        inputs.forEach((input: any) => {
          if (input.placeholder?.includes('কোড') || input.type === 'text') {
            input.value = '123456';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }, 0);
    } else if (state === 'forgot-mobile') {
      setMobile('01711111111');
      setTimeout(() => {
        const inputs = document.querySelectorAll('input');
        inputs.forEach((input: any) => {
          if (input.type === 'tel' || input.placeholder?.includes('017')) {
            input.value = '01711111111';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }, 0);
    } else if (state === 'forgot-final') {
      setPassword('pass1234');
      setConfirmPassword('pass1234');
      setTimeout(() => {
        const inputs = document.querySelectorAll('input');
        inputs.forEach((input: any) => {
          if (input.type === 'password' || input.placeholder?.includes('পাসওয়ার্ড')) {
            input.value = 'pass1234';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }, 0);
    }
  };

  // Centralized AJAX Router & Mock handler
  const executeApiCall = async (action: string, data: Record<string, string>) => {
    const shAuth = (window as any).shAuth;
    if (shAuth && shAuth.ajax_url && shAuth.nonce) {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('nonce', shAuth.nonce);
      Object.entries(data).forEach(([key, val]) => {
        formData.append(key, val);
      });

      const response = await fetch(normalizeAjaxUrl(shAuth.ajax_url), {
        method: 'POST',
        body: formData,
      });

       const res = await response.json();
      if (!res.success) {
        let errorMsg = 'অনাকাঙ্ক্ষিত ত্রুটি ঘটেছে।';
        if (typeof res.data === 'string') {
          errorMsg = res.data;
        } else if (res.data && typeof res.data.message === 'string') {
          errorMsg = res.data.message;
        } else if (res.data && typeof res.data.error === 'string') {
          errorMsg = res.data.error;
        } else if (typeof res.message === 'string') {
          errorMsg = res.message;
        }
        throw new Error(errorMsg);
      }
      return res.data;
    } else {
      // Mock Offline Vite Mode
      await new Promise(resolve => setTimeout(resolve, 600)); // Smooth transition preview
      switch (action) {
        case 'sh_send_otp':
          console.log(`[MOCK API] Sending OTP to ${data.mobile} for context: ${data.context}`);
          return 'ওটিপি (OTP) সফলভাবে পাঠানো হয়েছে। (টেস্ট কোড: 123456)';
        case 'sh_verify_otp':
          console.log(`[MOCK API] Verifying code ${data.code} for ${data.mobile}`);
          if (data.code !== '123456' && data.code !== '111111') {
            throw new Error('ভুল ভেরিফিকেশন কোড। অনুগ্রহ করে সঠিক কোড দিন (১২৩৪৫৬)।');
          }
          return 'মোবাইল নম্বর সফলভাবে যাচাই করা হয়েছে।';
        case 'sh_register':
          console.log('[MOCK API] Registering user:', data);
          return {
            message: 'নিবন্ধন সফল হয়েছে।',
            redirect: '/dashboard/'
          };
        case 'sh_login':
          console.log('[MOCK API] Logging in:', data);
          // Standard login logic for mockup testing
          if (data.mobile === '01711111111' && data.password !== 'pass1234') {
            throw new Error('মোবাইল নম্বর অথবা পাসওয়ার্ডটি সঠিক নয়। ডেমো পাসওয়ার্ড: pass1234');
          }
          return {
            message: 'লগইন সফল হয়েছে।',
            redirect: '/dashboard/'
          };
        case 'sh_forgot_password_reset':
          console.log('[MOCK API] Resetting password:', data);
          return {
            message: 'পাসওয়ার্ড রিসেট সফল হয়েছে।',
            redirect: '/dashboard/'
          };
        default:
          throw new Error('Unknown AJAX action');
      }
    }
  };

  // Diagnostic Layer for Demo Account Multi-Device Auth
  const analyzeLoginFailure = async (loginPayload: any, deviceId: string) => {
    console.log(`[Auth-Telemetry] Initiating login analysis for Device: ${deviceId}`);
    
    // Test 1: Check for hidden/malformed characters in the Demo credential string
    const demoKeyRegex = /[\u0980-\u09FF]/; // Detects Bengali script if leaked into raw fields
    if (demoKeyRegex.test(JSON.stringify(loginPayload))) {
      console.warn("CRITICAL: Localization characters (যেমন: '🔑 ডেমো অ্যাকাউন্ট') leaked into raw payload keys!");
    }
    
    return { status: "PROCEED", reason: "Payload structure structural integrity passed." };
  };

  // Flow handlers
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    // 1. Sanitize input to strip any unexpected UI text accidentally copied with the credentials
    let cleanMobile = mobile.trim();
    const mobileMatch = cleanMobile.match(/01[3-9]\d{8}/);
    if (mobileMatch) {
      cleanMobile = mobileMatch[0];
    }
    const cleanPassword = password.trim();

    // Update state to match clean credentials in inputs
    setMobile(cleanMobile);
    setPassword(cleanPassword);

    // 2. Telemetry and encoding diagnostics
    await analyzeLoginFailure({ mobile: cleanMobile, password: cleanPassword }, 'client-device');

    const isDemoAccountName = cleanMobile === 'demo_instructor' || cleanMobile === 'demo_student';
    if (!isDemoAccountName && !/^01[3-9]\d{8}$/.test(cleanMobile)) {
      setError('মোবাইল নম্বরটি সঠিক নয়। অনুগ্রহ করে ১১ ডিজিটের সঠিক মোবাইল নম্বর দিন।');
      return;
    }
    if (!cleanPassword) {
      setError('অনুগ্রহ করে পাসওয়ার্ডটি প্রদান করুন।');
      return;
    }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_login', { mobile: cleanMobile, password: cleanPassword });
      
      if (res && res.requires_2fa) {
        setUserId(res.user_id);
        setMobileHint(res.mobile_hint);
        setSuccessMsg(res.message || 'আপনার মোবাইলে একটি ভেরিফিকেশন কোড পাঠানো হয়েছে।');
        setOtpCode(''); // Reset OTP input field
        setState('2fa');
        setLoading(false);
        return;
      }

      setSuccessMsg(res.message || 'লগইন সফল হয়েছে। অপেক্ষা করুন...');
      setTimeout(() => {
        setLoading(false);
        onLoginSuccess(res?.redirect);
        onClose();
      }, 1000);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'নেটওয়ার্ক ত্রুটি ঘটেছে।');
    }
  };

  const handleRegisterInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    // Sanitize input
    let cleanMobile = mobile.trim();
    const mobileMatch = cleanMobile.match(/01[3-9]\d{8}/);
    if (mobileMatch) {
      cleanMobile = mobileMatch[0];
    }
    setMobile(cleanMobile);

    if (!name.trim()) {
      setError('অনুগ্রহ করে আপনার সম্পূর্ণ নাম লিখুন।');
      return;
    }
    if (!/^01[3-9]\d{8}$/.test(cleanMobile)) {
      setError('মোবাইল নম্বরটি সঠিক নয়। অনুগ্রহ করে ১১ ডিজিটের সঠিক মোবাইল নম্বর দিন।');
      return;
    }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_send_otp', { mobile: cleanMobile, name, context: 'register' });
      setSuccessMsg(res || 'ওটিপি পাঠানো হয়েছে।');
      setAuthContext('register');
      setLoading(false);
      setState('otp');
      startOtpCooldown();
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'ওটিপি পাঠাতে ব্যর্থ হয়েছে।');
    }
  };

  const handleForgotMobileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    // Sanitize input
    let cleanMobile = mobile.trim();
    const mobileMatch = cleanMobile.match(/01[3-9]\d{8}/);
    if (mobileMatch) {
      cleanMobile = mobileMatch[0];
    }
    setMobile(cleanMobile);

    if (!/^01[3-9]\d{8}$/.test(cleanMobile)) {
      setError('মোবাইল নম্বরটি সঠিক নয়। অনুগ্রহ করে ১১ ডিজিটের সঠিক মোবাইল নম্বর দিন।');
      return;
    }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_send_otp', { mobile: cleanMobile, context: 'forgot' });
      setSuccessMsg(res || 'ওটিপি পাঠানো হয়েছে।');
      setAuthContext('forgot');
      setLoading(false);
      setState('otp');
      startOtpCooldown();
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'ওটিপি পাঠাতে ব্যর্থ হয়েছে।');
    }
  };

  const handleOtpVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!/^\d{6}$/.test(otpCode)) {
      setError('অনুগ্রহ করে ৬ ডিজিটের সঠিক কোড প্রদান করুন।');
      return;
    }

    setLoading(true);
    try {
      await executeApiCall('sh_verify_otp', { mobile, code: otpCode });
      setLoading(false);
      setSuccessMsg('ওটিপি কোড সফলভাবে যাচাই করা হয়েছে।');
      if (authContext === 'register') {
        setState('register-final');
      } else {
        setState('forgot-final');
      }
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'ভেরিফিকেশন ব্যর্থ হয়েছে।');
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccessMsg('');
    setCountdown(120);

    try {
      const res = await executeApiCall('sh_send_otp', { mobile, context: authContext });
      setSuccessMsg(res || 'আপনার ফোনে একটি নতুন ভেরিফিকেশন কোড পাঠানো হয়েছে।');
    } catch (err: any) {
      setError(err.message || 'ওটিপি পাঠাতে ব্যর্থ হয়েছে।');
    }
  };

  const handle2faVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!/^\d{6}$/.test(otpCode)) {
      setError('অনুগ্রহ করে ৬ ডিজিটের সঠিক কোড প্রদান করুন।');
      return;
    }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_verify_2fa_otp', { user_id: String(userId), otp: otpCode });
      setLoading(false);
      setSuccessMsg(res.message || 'লগইন সফল হয়েছে। অপেক্ষা করুন...');
      setTimeout(() => {
        onLoginSuccess(res?.redirect_url || res?.redirect);
        onClose();
      }, 1000);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'ভেরিফিকেশন ব্যর্থ হয়েছে।');
    }
  };

  const handleResend2faOtp = async () => {
    setError('');
    setSuccessMsg('');
    try {
      const res = await executeApiCall('sh_resend_2fa_otp', { user_id: String(userId) });
      setSuccessMsg(res.message || 'নতুন ভেরিফিকেশন কোড পাঠানো হয়েছে।');
    } catch (err: any) {
      setError(err.message || 'ওটিপি পাঠাতে ব্যর্থ হয়েছে।');
    }
  };

  const handleRegisterFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (password.length < 6) {
      setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।');
      return;
    }
    if (password !== confirmPassword) {
      setError('পাসওয়ার্ড দুটি মেলেনি। অনুগ্রহ করে আবার চেষ্টা করুন।');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        mobile,
        name,
        class: selectedClass,
        batch: selectedBatch,
        group: selectedGroup,
        optional_subject: optionalSubject || 'none',
        institution: selectedInstitution,
        gender: gender,
        password: password
      };
      await executeApiCall('sh_register', payload);
      setLoading(false);
      setState('celebration');

      // Trigger Confetti
      setTimeout(() => {
        const win = window as any;
        if (typeof win.confetti === 'function') {
          win.confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }, 300);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'নিবন্ধন সম্পূর্ণ করতে ব্যর্থ হয়েছে।');
    }
  };

  const handleForgotFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (password.length < 6) {
      setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।');
      return;
    }
    if (password !== confirmPassword) {
      setError('পাসওয়ার্ড দুটি মেলেনি। অনুগ্রহ করে আবার চেষ্টা করুন।');
      return;
    }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_forgot_password_reset', { mobile, password });
      setSuccessMsg(res.message || 'পাসওয়ার্ড রিসেট সফল হয়েছে। আপনাকে লগইন করা হচ্ছে...');
      setTimeout(() => {
        setLoading(false);
        onLoginSuccess(res?.redirect);
        onClose();
      }, 1000);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'পাসওয়ার্ড পরিবর্তন করতে ব্যর্থ হয়েছে।');
    }
  };

  // Get active step index for Stepper rendering
  const getActiveStep = () => {
    if (state === 'register-info' || state === 'forgot-mobile') return 1;
    if (state === 'otp') return 2;
    if (state === 'register-final' || state === 'forgot-final') return 3;
    return 0;
  };

  const activeStep = getActiveStep();
  const showStepper = activeStep > 0 && state !== 'celebration';
  const progressPercent = activeStep === 1 ? '0%' : activeStep === 2 ? '50%' : '100%';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 cursor-pointer"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 15 }}
            transition={{ type: 'spring', duration: 0.4 }}
            className="relative w-full max-w-md bg-[#161616] border border-white/10 rounded-3xl overflow-hidden shadow-2xl z-10 text-left p-6 md:p-8"
          >
            {/* Background Glows */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.08),transparent_70%)] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.08),transparent_70%)] pointer-events-none" />

            {/* Close Button */}
            {state !== 'celebration' && (
              <button
                onClick={onClose}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/15 transition-all cursor-pointer"
              >
                <X className="w-4.5 h-4.5" />
              </button>
            )}

            {/* Title / Brand Header (Hidden in celebration or register-welcome view) */}
            {/* Title / Brand Header */}
            {state !== 'celebration' &&
             state !== 'login' &&
             state !== 'register-welcome' &&
             state !== 'register-class' &&
             state !== 'register-group' &&
             state !== 'register-batch' &&
             state !== 'register-gender' &&
             state !== 'register-institution' && (
              <div className="mb-6">
                <span className="font-bangla text-[10px] font-bold text-[#00CBA9] uppercase tracking-widest bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
                  চর্চাঘর পোর্টাল
                </span>
                <h3 className="font-bangla text-2xl font-black text-white mt-2">
                  {state === 'register-info' && 'নতুন অ্যাকাউন্ট খুলুন'}
                  {state === 'otp' && 'কোড যাচাই করুন'}
                  {state === 'register-final' && 'প্রায় শেষ!'}
                  {state === 'forgot-mobile' && 'পাসওয়ার্ড রিসেট'}
                  {state === 'forgot-final' && 'নতুন পাসওয়ার্ড'}
                  {state === '2fa' && 'নিরাপত্তা যাচাইকরণ'}
                </h3>
                <p className="font-bangla text-xs text-gray-400 mt-1">
                  {state === 'register-info' && 'ধাপ ১/৩: ব্যক্তিগত তথ্য'}
                  {state === 'otp' && 'ধাপ ২/৩: মোবাইল নম্বর যাচাই করুন'}
                  {state === 'register-final' && 'ধাপ ৩/৩: একাডেমিক তথ্য'}
                  {state === 'forgot-mobile' && 'ধাপ ১/৩: অ্যাকাউন্ট খুঁজুন'}
                  {state === 'forgot-final' && 'ধাপ ৩/৩: পাসওয়ার্ড সেট করুন'}
                  {state === '2fa' && 'লগইন করতে মোবাইল ভেরিফিকেশন কোড দিন'}
                </p>
              </div>
            )}

            {/* Registration/Forgot Stepper Progress Indicator */}
            {showStepper && (
              <div className="w-full max-w-sm mx-auto mb-8 px-1">
                <div className="flex items-center justify-between relative">
                  {/* Background line */}
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-white/10 rounded-full z-0"></div>
                  {/* Dynamic progress fill line */}
                  <div
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-[#00CBA9] rounded-full z-0 transition-all duration-500 ease-in-out shadow-[0_0_8px_rgba(0,203,169,0.5)]"
                    style={{ width: progressPercent }}
                  />

                  {/* Step 1: Account */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        activeStep > 1
                          ? 'bg-[#00CBA9] border-[#00CBA9] text-black font-bold'
                          : activeStep === 1
                          ? 'bg-[#161616] border-[#00CBA9] text-[#00CBA9] shadow-[0_0_10px_rgba(0,203,169,0.3)]'
                          : 'bg-[#161616] border-white/20 text-gray-500'
                      }`}
                    >
                      {activeStep > 1 ? <Check className="w-4.5 h-4.5 stroke-[3px]" /> : <User className="w-4.5 h-4.5" />}
                    </div>
                    <span className={`font-bangla text-[10px] mt-1.5 font-bold ${activeStep >= 1 ? 'text-[#00CBA9]' : 'text-gray-500'}`}>
                      অ্যাকাউন্ট
                    </span>
                  </div>

                  {/* Step 2: Verification */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        activeStep > 2
                          ? 'bg-[#00CBA9] border-[#00CBA9] text-black font-bold'
                          : activeStep === 2
                          ? 'bg-[#161616] border-[#00CBA9] text-[#00CBA9] shadow-[0_0_10px_rgba(0,203,169,0.3)]'
                          : 'bg-[#161616] border-white/20 text-gray-500'
                      }`}
                    >
                      {activeStep > 2 ? <Check className="w-4.5 h-4.5 stroke-[3px]" /> : <Lock className="w-4.5 h-4.5" />}
                    </div>
                    <span className={`font-bangla text-[10px] mt-1.5 font-bold ${activeStep >= 2 ? 'text-[#00CBA9]' : 'text-gray-500'}`}>
                      যাচাই
                    </span>
                  </div>

                  {/* Step 3: Academic/Password */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        activeStep === 3
                          ? 'bg-[#161616] border-[#00CBA9] text-[#00CBA9] shadow-[0_0_10px_rgba(0,203,169,0.3)]'
                          : 'bg-[#161616] border-white/20 text-gray-500'
                      }`}
                    >
                      {authContext === 'register' ? (
                        <GraduationCap className="w-4.5 h-4.5" />
                      ) : (
                        <Lock className="w-4.5 h-4.5" />
                      )}
                    </div>
                    <span className={`font-bangla text-[10px] mt-1.5 font-bold ${activeStep === 3 ? 'text-[#00CBA9]' : 'text-gray-500'}`}>
                      {authContext === 'register' ? 'একাডেমিক' : 'নতুন পাসওয়ার্ড'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Demo Helper bar */}
            {state !== 'celebration' && state !== 'register-welcome' && (
              <div className="mb-4 p-3 rounded-2xl bg-white/5 border border-white/5 flex flex-col gap-2.5">
                <div>
                  <p className="font-bangla text-[10px] text-gray-400">সহজে টেস্ট করার জন্য ডেমো তথ্য:</p>
                  {state === 'login' ? (
                    <div className="flex flex-col gap-0.5 mt-1">
                      <p className="font-english text-[10px] text-gray-500 font-semibold">
                        Student: <span className="text-gray-300">01711111111</span> (পাসওয়ার্ড: <span className="text-gray-300">pass1234</span>)
                      </p>
                      <p className="font-english text-[10px] text-gray-500 font-semibold">
                        Instructor: <span className="text-gray-300">01712345678</span> (পাসওয়ার্ড: <span className="text-gray-300">instructor123</span>)
                      </p>
                    </div>
                  ) : (
                    <p className="font-english text-[10px] text-gray-500 truncate font-semibold mt-1">
                      {state === 'register-info' && 'রাহুল আহমেদ | 01711111111'}
                      {state === 'otp' && 'OTP কোড: 123456'}
                      {state === 'forgot-mobile' && 'মোবাইল: 01711111111'}
                      {state === 'forgot-final' && 'যেকোনো ৬+ সংখ্যার পাসওয়ার্ড'}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {state === 'login' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => triggerDomFill('01711111111', 'pass1234')}
                        className="flex-1 py-1 text-[10px] font-bangla font-semibold text-[#00CBA9] hover:text-[#00b092] bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all rounded-lg cursor-pointer"
                      >
                        স্টুডেন্ট অটো-ফিল
                      </button>
                      <button
                        type="button"
                        onClick={() => triggerDomFill('01712345678', 'instructor123')}
                        className="flex-1 py-1 text-[10px] font-bangla font-semibold text-sky-400 hover:text-sky-300 bg-sky-500/10 border border-sky-500/20 hover:bg-sky-500/20 transition-all rounded-lg cursor-pointer"
                      >
                        শিক্ষক অটো-ফিল
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAutoFill}
                      className="w-full py-1 text-[10px] font-bangla font-semibold text-[#00CBA9] hover:text-[#00b092] bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all rounded-lg cursor-pointer text-center"
                    >
                      অটো-ফিল
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/15 border border-red-500/30 text-red-400 rounded-2xl flex items-start gap-2.5 text-xs font-bangla"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Success Message */}
            {successMsg && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 rounded-2xl flex items-start gap-2.5 text-xs font-bangla"
              >
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{successMsg}</span>
              </motion.div>
            )}

            {/* Auth Form State Router */}
            <AnimatePresence mode="wait">
              {/* STATE: Register Welcome */}
              {state === 'register-welcome' && (
                <motion.div
                  key="register-welcome"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('login')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[220px] mx-auto text-center font-poppins">
                      HI, I am Cheero
                      {/* Arrow of speech bubble */}
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot Image container with pulsing circle glow background */}
                    <div className="w-48 h-48 mx-auto mb-8 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl shadow-[0_0_20px_rgba(255,255,255,0.05)] border border-white/5" alt="Mascot" />
                    </div>

                    {/* Action button to proceed to info entry */}
                    <button
                      type="button"
                      onClick={() => setState('register-class')}
                      className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <span className="font-bangla">এগিয়ে যাও</span>
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STATE: Register Step A (Class Selection) */}
              {state === 'register-class' && (
                <motion.div
                  key="register-class"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('register-welcome')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তুমি কি বিষয়ে চর্চা করতে চাও?
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    {/* Class Options */}
                    <div className="w-full space-y-3">
                      {[
                        { val: 'class_6_8', label: 'ক্লাস ৬-৮', icon: '📚', bg: 'bg-indigo-500/10 text-indigo-400' },
                        { val: 'ssc', label: 'এসএসসি', icon: '🎒', bg: 'bg-emerald-500/10 text-emerald-400' },
                        { val: 'dakhil', label: 'দাখিল', icon: '🕌', bg: 'bg-amber-500/10 text-amber-400' },
                        { val: 'alim', label: 'আলিম', icon: '📖', bg: 'bg-rose-500/10 text-rose-400' },
                        { val: 'hsc', label: 'এইচএসসি / অ্যাডমিশন', icon: '🎓', bg: 'bg-sky-500/10 text-sky-400' },
                        { val: 'bcs', label: 'বিসিএস / জবস', icon: '💼', bg: 'bg-purple-500/10 text-purple-400' }
                      ].map(opt => (
                        <button
                          key={opt.val}
                          type="button"
                          onClick={() => {
                            setSelectedClass(opt.val);
                            if (['ssc', 'dakhil', 'alim', 'hsc'].includes(opt.val)) {
                              setState('register-group');
                            } else {
                              setSelectedGroup('general');
                              setState('register-batch');
                            }
                          }}
                          className="w-full flex items-center justify-between p-3.5 bg-white/5 hover:bg-[#00CBA9]/10 border border-white/10 hover:border-[#00CBA9]/30 rounded-2xl transition-all group text-left cursor-pointer focus:outline-none"
                        >
                          <span className="flex items-center gap-3.5 text-sm font-bold text-white font-bangla">
                            <span className={`w-10 h-10 ${opt.bg} rounded-xl flex items-center justify-center text-lg font-black group-hover:scale-105 transition-transform`}>{opt.icon}</span>
                            {opt.label}
                          </span>
                          <span className="text-gray-400 group-hover:text-white transition-colors">→</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STATE: Register Step B (Group Selection) */}
              {state === 'register-group' && (
                <motion.div
                  key="register-group"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('register-class')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তুমি কি বিভাগে চর্চা করতে চাও?
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    {/* Group Options */}
                    <div className="w-full space-y-3">
                      {[
                        { val: 'science', label: 'বিজ্ঞান', icon: '🔬', bg: 'bg-cyan-500/10 text-cyan-400' },
                        { val: 'humanities', label: 'সাধারণ বিভাগ', icon: '🌍', bg: 'bg-emerald-500/10 text-emerald-400' },
                        { val: 'commerce', label: 'বাণিজ্য', icon: '💼', bg: 'bg-indigo-500/10 text-indigo-400' }
                      ].map(opt => (
                        <button
                          key={opt.val}
                          type="button"
                          onClick={() => {
                            setSelectedGroup(opt.val);
                            setState('register-batch');
                          }}
                          className="w-full flex items-center justify-between p-3.5 bg-white/5 hover:bg-[#00CBA9]/10 border border-white/10 hover:border-[#00CBA9]/30 rounded-2xl transition-all group text-left cursor-pointer focus:outline-none"
                        >
                          <span className="flex items-center gap-3.5 text-sm font-bold text-white font-bangla">
                            <span className={`w-10 h-10 ${opt.bg} rounded-xl flex items-center justify-center text-lg font-black group-hover:scale-105 transition-transform`}>{opt.icon}</span>
                            {opt.label}
                          </span>
                          <span className="text-gray-400 group-hover:text-white transition-colors">→</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STATE: Register Step C (Batch Selection) */}
              {state === 'register-batch' && (
                <motion.div
                  key="register-batch"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        if (['class_6_8', 'bcs'].includes(selectedClass)) {
                          setState('register-class');
                        } else {
                          setState('register-group');
                        }
                      }}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তোমার ব্যাচ ?
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    {/* Batch Options */}
                    <div className="w-full space-y-3">
                      {(selectedClass === 'dakhil'
                        ? [
                            { slug: 'dakhil-2026', label: 'DAKHIL 2026' },
                            { slug: 'dakhil-2027', label: 'DAKHIL 2027' },
                            { slug: 'dakhil-2028', label: 'DAKHIL 2028' }
                          ]
                        : selectedClass === 'ssc'
                        ? [
                            { slug: 'ssc-2027', label: 'SSC 2027' },
                            { slug: 'ssc-2028', label: 'SSC 2028' }
                          ]
                        : selectedClass === 'alim'
                        ? [
                            { slug: 'alim-2026', label: 'ALIM 2026' },
                            { slug: 'alim-2027', label: 'ALIM 2027' },
                            { slug: 'alim-2028', label: 'ALIM 2028' }
                          ]
                        : selectedClass === 'hsc'
                        ? [
                            { slug: 'hsc-2026', label: 'HSC 2026' },
                            { slug: 'hsc-2027', label: 'HSC 2027' }
                          ]
                        : selectedClass === 'class_6_8'
                        ? [
                            { slug: 'class-6', label: 'Class 6' },
                            { slug: 'class-7', label: 'Class 7' },
                            { slug: 'class-8', label: 'Class 8' }
                          ]
                        : [
                            { slug: 'bcs-47', label: 'BCS 47th' },
                            { slug: 'bcs-48', label: 'BCS 48th' }
                          ]
                      ).map(opt => (
                        <button
                          key={opt.slug}
                          type="button"
                          onClick={() => {
                            setSelectedBatch(opt.slug);
                            setState('register-institution');
                          }}
                          className="w-full flex items-center justify-center p-4 bg-white/5 hover:bg-[#00CBA9]/10 border border-white/10 hover:border-[#00CBA9]/30 rounded-2xl transition-all font-bold text-white text-base cursor-pointer focus:outline-none"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STATE: Register Step E (Institution Selection) */}
              {state === 'register-institution' && (
                <motion.div
                  key="register-institution"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('register-batch')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তোমার প্রতিষ্ঠান কোনটি ?
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    {/* Institution Input Form */}
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (selectedInstitution.trim()) {
                          setState('register-gender');
                        }
                      }}
                      className="w-full space-y-4 text-left"
                    >
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-300">Institution</label>
                        <input
                          type="text"
                          value={selectedInstitution}
                          onChange={(e) => setSelectedInstitution(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-xs font-bangla text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          placeholder="প্রতিষ্ঠানের নাম লিখুন"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <span className="font-bangla">এগিয়ে যাও</span>
                      </button>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* STATE: Register Step D (Gender Selection) */}
              {state === 'register-gender' && (
                <motion.div
                  key="register-gender"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4 text-left"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('register-institution')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তুমি একজন
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    {/* Gender Options */}
                    <div className="w-full grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setGender('male');
                          setState('register-info');
                        }}
                        className="flex flex-col items-center p-5 bg-white/5 hover:bg-[#00CBA9]/10 border border-white/10 hover:border-[#00CBA9]/30 rounded-2xl transition-all group cursor-pointer focus:outline-none"
                      >
                        <span className="w-16 h-16 bg-blue-500/10 text-blue-400 rounded-full flex items-center justify-center text-3xl mb-3 group-hover:scale-105 transition-transform">👦</span>
                        <span className="text-sm font-bold text-white font-bangla">ছাত্র</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setGender('female');
                          setState('register-info');
                        }}
                        className="flex flex-col items-center p-5 bg-white/5 hover:bg-[#00CBA9]/10 border border-white/10 hover:border-[#00CBA9]/30 rounded-2xl transition-all group cursor-pointer focus:outline-none"
                      >
                        <span className="w-16 h-16 bg-pink-500/10 text-pink-400 rounded-full flex items-center justify-center text-3xl mb-3 group-hover:scale-105 transition-transform">👧</span>
                        <span className="text-sm font-bold text-white font-bangla">ছাত্রী</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STATE: Login */}
              {state === 'login' && (
                <motion.form
                  key="login-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleLoginSubmit}
                  className="space-y-4 w-full"
                >
                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[250px] mx-auto text-center font-bangla">
                      চর্চা করতে আজই আপনার অ্যাকাউন্টে প্রবেশ করুন
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    <div className="w-full space-y-4 text-left">
                      <div className="space-y-1.5">
                        <label className="font-bangla text-xs font-semibold text-gray-300">মোবাইল নম্বর</label>
                        <div className="relative">
                          <input
                            type="tel"
                            name="mobile"
                            placeholder="যেমন: 017XXXXXXXX"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            required
                          />
                          <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <label className="font-bangla text-xs font-semibold text-gray-300">পাসওয়ার্ড</label>
                          <button
                            type="button"
                            onClick={() => setState('forgot-mobile')}
                            className="font-bangla text-[10px] text-gray-400 hover:text-[#00CBA9] transition-colors focus:outline-none cursor-pointer"
                          >
                            পাসওয়ার্ড ভুলে গেছেন?
                          </button>
                        </div>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="আপনার পাসওয়ার্ড দিন"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-10 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            required
                          />
                          <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3.5 top-3.5 text-gray-500 hover:text-white focus:outline-none cursor-pointer"
                          >
                            {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                          </button>
                        </div>
                      </div>

                      {/* Demo Quick Autofill */}
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-3 space-y-2">
                        <div className="flex justify-start items-center">
                          <span className="font-bangla text-[10px] text-gray-400 font-medium">সহজে টেস্ট করুন (ক্লিক করুন)</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setMobile('01711111111');
                              setPassword('pass1234');
                            }}
                            className="py-2 px-3 bg-white/5 hover:bg-white/10 text-white rounded-xl border border-white/5 hover:border-white/10 text-[11.5px] font-bangla transition-all duration-200 cursor-pointer flex items-center justify-center gap-1.5 active:scale-[0.97]"
                          >
                            <User className="w-3.5 h-3.5 text-emerald-400" />
                            <span>শিক্ষার্থী লগইন</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMobile('01712345678');
                              setPassword('instructor123');
                            }}
                            className="py-2 px-3 bg-white/5 hover:bg-white/10 text-white rounded-xl border border-white/5 hover:border-white/10 text-[11.5px] font-bangla transition-all duration-200 cursor-pointer flex items-center justify-center gap-1.5 active:scale-[0.97]"
                          >
                            <User className="w-3.5 h-3.5 text-sky-400" />
                            <span>শিক্ষক লগইন</span>
                          </button>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-4"
                      >
                        {loading ? (
                          <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                        ) : (
                          <span>লগইন করুন</span>
                        )}
                      </button>

                      <div className="pt-4 border-t border-white/5 text-center text-xs text-gray-500">
                        অ্যাকাউন্ট নেই?{' '}
                        <button
                          type="button"
                          onClick={() => setState('register-welcome')}
                          className="text-[#00CBA9] font-bold hover:underline focus:outline-none cursor-pointer"
                        >
                          নতুন অ্যাকাউন্ট খুলুন
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.form>
              )}

              {/* STATE: Register Info (Step 1) */}
              {state === 'register-info' && (
                <motion.form
                  key="register-info-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleRegisterInfoSubmit}
                  className="space-y-4"
                >
                  <div className="space-y-1.5 text-left">
                    <label className="font-bangla text-xs font-semibold text-gray-300">আপনার নাম</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="যেমন: রাহুল আহমেদ"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-xs font-bangla text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />
                      <User className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                    </div>
                  </div>

                  <div className="space-y-1.5 text-left">
                    <label className="font-bangla text-xs font-semibold text-gray-300">মোবাইল নম্বর</label>
                    <div className="relative">
                      <input
                        type="tel"
                        placeholder="যেমন: 017XXXXXXXX"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />
                      <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-4"
                  >
                    {loading ? (
                      <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                    ) : (
                      <span>ওটিপি পাঠান</span>
                    )}
                  </button>

                  <div className="pt-4 border-t border-white/5 flex justify-between items-center text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={() => setState('register-welcome')}
                      className="text-gray-400 hover:text-white transition-colors focus:outline-none flex items-center gap-1 cursor-pointer"
                    >
                      ← ফিরে যান
                    </button>
                    <div>
                      ইতিমধ্যে অ্যাকাউন্ট আছে?{' '}
                      <button
                        type="button"
                        onClick={() => setState('login')}
                        className="text-[#00CBA9] font-bold hover:underline focus:outline-none cursor-pointer"
                      >
                        লগইন করুন
                      </button>
                    </div>
                  </div>
                </motion.form>
              )}

              {/* STATE: Forgot Password Mobile lookup */}
              {state === 'forgot-mobile' && (
                <motion.form
                  key="forgot-mobile-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleForgotMobileSubmit}
                  className="space-y-4"
                >
                  <div className="space-y-1.5 text-left">
                    <label className="font-bangla text-xs font-semibold text-gray-300">নিবন্ধিত মোবাইল নম্বর</label>
                    <div className="relative">
                      <input
                        type="tel"
                        placeholder="যেমন: 017XXXXXXXX"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />
                      <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-4"
                  >
                    {loading ? (
                      <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                    ) : (
                      <span>রিসেট কোড পাঠান</span>
                    )}
                  </button>

                  <div className="pt-4 border-t border-white/5 text-center text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={() => setState('login')}
                      className="text-[#00CBA9] font-bold hover:underline focus:outline-none flex items-center justify-center gap-1 mx-auto"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> লগইনে ফিরে যান
                    </button>
                  </div>
                </motion.form>
              )}

              {/* STATE: OTP Verification */}
              {state === 'otp' && (
                <motion.form
                  key="otp-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleOtpVerifySubmit}
                  className="space-y-5"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        if (authContext === 'register') setState('register-info');
                        if (authContext === 'forgot') setState('forgot-mobile');
                      }}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      কোড যাচাই করুন
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    <div className="text-center mb-6">
                      <p className="font-bangla text-xs text-gray-400">প্রদত্ত মোবাইল নম্বরে পাঠানো ৬-ডিজিটের কোডটি দিন</p>
                      <p className="font-english font-bold text-white text-base mt-1.5">{mobile}</p>
                    </div>

                    <div className="w-full space-y-4">
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="------"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 text-center text-2xl font-semibold tracking-widest font-mono text-white placeholder-gray-600 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {loading ? (
                          <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                        ) : (
                          <span>কোড যাচাই করুন</span>
                        )}
                      </button>

                      {/* Circular Timer & Resend code section */}
                      <div className="flex flex-col items-center justify-center gap-3 py-2">
                        <div className="relative w-12 h-12 flex items-center justify-center">
                          <svg className="w-12 h-12 -rotate-90">
                            <circle
                              cx="24"
                              cy="24"
                              r="18"
                              className="stroke-white/10 fill-none stroke-[3px]"
                            />
                            <circle
                              cx="24"
                              cy="24"
                              r="18"
                              className="stroke-[#00CBA9] fill-none stroke-[3px] stroke-linecap-round transition-all duration-1000 ease-linear"
                              strokeDasharray={113.1}
                              strokeDashoffset={113.1 - (countdown / 120) * 113.1}
                            />
                          </svg>
                          <span className="absolute font-mono text-[10px] text-white font-bold">{countdown}</span>
                        </div>

                        <button
                          type="button"
                          disabled={countdown > 0}
                          onClick={handleResendOtp}
                          className="text-xs font-bangla font-semibold text-[#00CBA9] hover:text-[#00b092] disabled:opacity-40 disabled:hover:text-[#00CBA9] transition-colors focus:outline-none cursor-pointer"
                        >
                          কোড পুনরায় পাঠান
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.form>
              )}

              {/* STATE: 2FA Verification */}
              {state === '2fa' && (
                <motion.form
                  key="2fa-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handle2faVerifySubmit}
                  className="space-y-5"
                >
                  <div className="text-center">
                    <p className="font-bangla text-xs text-gray-400">আপনার মোবাইলে পাঠানো ৬-ডিজিটের ভেরিফিকেশন কোডটি দিন</p>
                    <p className="font-english font-bold text-white text-base mt-1.5">{mobileHint}</p>
                  </div>

                  <div className="space-y-1.5 text-left">
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="------"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 text-center text-2xl font-semibold tracking-widest font-mono text-white placeholder-gray-600 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                      required
                    />
                  </div>

                  <div className="flex flex-col items-center justify-center gap-3 py-2">
                    <button
                      type="button"
                      onClick={handleResend2faOtp}
                      className="text-xs font-bangla font-semibold text-[#00CBA9] hover:text-[#00b092] transition-colors focus:outline-none cursor-pointer"
                    >
                      কোড পুনরায় পাঠান
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading ? (
                      <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                    ) : (
                      <span>কোড যাচাই করুন</span>
                    )}
                  </button>

                  <div className="pt-2 text-center text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={() => setState('login')}
                      className="text-[#00CBA9] font-bold hover:underline focus:outline-none flex items-center justify-center gap-1 mx-auto"
                    >
                      ← ফিরে যান
                    </button>
                  </div>
                </motion.form>
              )}

              {/* STATE: Register Final (Academic) */}
              {/* STATE: Register Final (Password Creation) */}
              {state === 'register-final' && (
                <motion.form
                  key="register-final-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleRegisterFinalSubmit}
                  className="space-y-4"
                >
                  <div className="flex justify-start mb-6">
                    <button
                      type="button"
                      onClick={() => setState('register-info')}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-bangla">ফিরে যান</span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center py-2">
                    {/* Speech Bubble */}
                    <div className="relative bg-white text-black px-5 py-2.5 rounded-2xl shadow-xl font-bold text-sm mb-6 max-w-[225px] mx-auto text-center font-bangla">
                      তোমার পাসওয়ার্ড ?
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white"></div>
                    </div>

                    {/* Mascot */}
                    <div className="w-28 h-28 mx-auto mb-6 flex items-center justify-center relative">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.12),transparent_70%)] pointer-events-none rounded-full animate-pulse" />
                      <img src={mascot2} className="max-w-full max-h-full object-contain rounded-2xl" alt="Mascot" />
                    </div>

                    <div className="w-full space-y-4 text-left">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-300">Password</label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-xs font-bangla text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          placeholder="পাসওয়ার্ড লিখো"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-300">Confirm Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-xs font-bangla text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          placeholder="পাসওয়ার্ড নিশ্চিত করো"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-4"
                      >
                        {loading ? (
                          <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                        ) : (
                          <span>অ্যাকাউন্ট তৈরি করো</span>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.form>
              )}

              {/* STATE: Forgot Password Final */}
              {state === 'forgot-final' && (
                <motion.form
                  key="forgot-final-form"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onSubmit={handleForgotFinalSubmit}
                  className="space-y-4"
                >
                  <div className="space-y-1.5 text-left font-bangla">
                    <label className="text-xs font-semibold text-gray-300">নতুন পাসওয়ার্ড</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="নতুন পাসওয়ার্ড দিন"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-10 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />
                      <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-3.5 text-gray-500 hover:text-white focus:outline-none cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-left font-bangla">
                    <label className="text-xs font-semibold text-gray-300">পাসওয়ার্ড নিশ্চিত করুন</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="পাসওয়ার্ড নিশ্চিত করুন"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-10 py-3 text-xs font-english text-white placeholder-gray-500 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                        required
                      />
                      <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3.5 top-3.5 text-gray-500 hover:text-white focus:outline-none cursor-pointer"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-4"
                  >
                    {loading ? (
                      <div className="w-5 h-5 rounded-full border-2 border-black border-t-transparent animate-spin" />
                    ) : (
                      <span>পাসওয়ার্ড সংরক্ষণ ও লগইন</span>
                    )}
                  </button>
                </motion.form>
              )}

              {/* STATE: Celebration */}
              {state === 'celebration' && (
                <motion.div
                  key="celebration-panel"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6 flex flex-col items-center"
                >
                  {/* Celebration Trophy/Sparkle Icon wrapper */}
                  <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
                    {/* Animated Pulsing Ring */}
                    <div className="absolute inset-0 bg-[#00CBA9]/10 rounded-full animate-ping pointer-events-none" />
                    <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-4 border-[#00CBA9]/30 flex items-center justify-center text-[#00CBA9] shadow-[0_0_20px_rgba(0,203,169,0.2)]">
                      <Sparkles className="w-10 h-10" />
                    </div>
                  </div>

                  <h3 className="font-bangla text-2xl font-black text-white">
                    স্বাগতম, {name || 'শিক্ষার্থী'}!
                  </h3>
                  <p className="font-bangla text-sm text-gray-400 mt-2 px-2 leading-relaxed">
                    আপনার অ্যাকাউন্ট সফলভাবে তৈরি হয়েছে এবং চর্চা ইকোসিস্টেমের সাথে যুক্ত হয়েছে। পড়াশোনা শুরু করার জন্য আপনি প্রস্তুত!
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      onLoginSuccess('/dashboard/');
                      onClose();
                    }}
                    className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b092] text-black font-bangla text-sm font-extrabold rounded-2xl transition-all duration-200 shadow-lg shadow-[#00CBA9]/15 active:scale-[0.95] flex items-center justify-center gap-2 cursor-pointer mt-8"
                  >
                    <span>ড্যাশবোর্ডে যান</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
